import { useField } from 'formik';
import { parseInt } from 'lodash';
import moment from 'moment';
import QuillDelta from 'quill-delta';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	CalendarIcon,
	Card,
	DatePicker,
	DraggableCard,
	FlexGap,
	Heading,
	Input,
	Label,
	NumericInput,
	Popover,
	PropertyChip,
	ReorderRenderState,
	Skeleton,
	Subheading,
	tokens,
} from '@treams/treams-ui';

function SubgoalDueDatePicker(props: { value?: Date; onChange: (date?: Date) => void }) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const empty = t('datePicker.value.empty');
	const label = props.value
		? moment(props.value).format('LL')
		: `${t('goals.show.dueDate.label')}: ${empty}`;

	return (
		<Popover.Target
			visible={visible}
			onDismiss={() => setVisible(false)}
			contents={
				<DatePicker
					value={props.value}
					onChange={props.onChange}
					onDismiss={() => setVisible(false)}
				/>
			}
		>
			<PropertyChip icon={<CalendarIcon />} label={label} onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}

export default Skeleton(
	function EditSubgoalCard(props: {
		subgoalID: string;
		isNew?: boolean;
		type?: graphql.GoalChecklistItemType;
		dragHandleProps?: ReorderRenderState['dragHandleProps'];
		isDragging?: boolean;
		onDismiss: (success: boolean) => void;
	}) {
		const { localize, language } = useLocalize();
		const { t } = useTranslation();

		const [{ value }, , { setValue }] = useField<graphql.GoalChecklistItem>(
			`subgoals.${props.subgoalID}`,
		);

		const [dueDate, setDueDate] = useState<Date | undefined>(value?.dueDate);
		const [title, setTitle] = useState(localize(value?.title ?? [])?.text ?? '');
		const [progressValue, setProgressValue] = useState(value?.progress.value?.toString() ?? '0');
		const [upper, setUpper] = useState(value?.progress.upper?.toString());

		const type = value?.progress.type ?? props.type;
		const showProgress =
			type === graphql.GoalChecklistItemType.Currency ||
			type === graphql.GoalChecklistItemType.Range;
		const titlePlaceholderKey =
			type === graphql.GoalChecklistItemType.Range ||
			type === graphql.GoalChecklistItemType.Percentage
				? 'goals.subgoals.edit.title.placeholder.measurable'
				: 'goals.subgoals.edit.title.placeholder';

		const save = () => {
			const combinedValue = progressValue ? parseInt(progressValue) : value?.progress.value;

			const progress =
				type === graphql.GoalChecklistItemType.Checkbox ||
				type === graphql.GoalChecklistItemType.Learning
					? {
							type: value?.progress.type ?? props.type,
							value:
								type === graphql.GoalChecklistItemType.Learning
									? (value?.progress.value ?? 0)
									: combinedValue,
							lower: undefined,
							upper: undefined,
						}
					: {
							type: value?.progress.type ?? props.type,
							value: combinedValue,
							lower: props.isNew ? combinedValue : value?.progress.lower,
							upper: upper ? parseInt(upper) : 1,
						};

			setValue({
				...value,
				title: [{ languageID: language.id, text: title }],
				description: [{ languageID: language.id, text: new QuillDelta() }],
				progress: progress as any,
				dueDate,
				createdAt: new Date(),
			});
			props.onDismiss(true);
		};

		return (
			<DraggableCard
				dragHandleProps={props.dragHandleProps ?? undefined}
				isDragging={props.isDragging}
				isDragDisabled={!props.dragHandleProps}
			>
				<FlexGap
					flexDirection="column"
					gap={tokens.gap.element}
					flexGrow={1}
					minWidth={0}
					width="100%"
				>
					{props.type && props.isNew && (
						<Subheading>
							{t(`goals.subgoals.create.heading.${props.type.toLowerCase()}`)}
						</Subheading>
					)}
					{showProgress && (
						<Flex>
							<Box width={3 / 12} mr={2}>
								<Label mb={`${tokens.gap.inline}px`}>
									{props.isNew
										? t('goals.subgoals.edit.lower.label')
										: t('goals.subgoals.edit.value.label')}
								</Label>
								<NumericInput
									prefix={type === graphql.GoalChecklistItemType.Currency ? '€' : undefined}
									value={progressValue ?? '0'}
									// @ts-ignore
									onChange={setProgressValue}
									mergeWith="right"
								/>
							</Box>
							<Box width={3 / 12}>
								<Label mb={`${tokens.gap.inline}px`}>{t('goals.subgoals.edit.upper.label')} </Label>
								<NumericInput
									prefix={type === graphql.GoalChecklistItemType.Currency ? '€' : undefined}
									value={upper ?? '0'}
									onChange={setUpper}
									mergeWith="left"
								/>
							</Box>
						</Flex>
					)}
					<FlexGap alignItems="center" gap={tokens.gap.element} width="100%" minWidth={0}>
						<Box flexGrow={1} flexShrink={1} minWidth={0}>
							<Input
								autoFocus
								width="100%"
								value={title}
								onChange={setTitle}
								placeholder={t(titlePlaceholderKey)}
							/>
						</Box>
						<SubgoalDueDatePicker value={dueDate} onChange={setDueDate} />
						<Button
							type="secondary"
							title={t('goals.subgoals.cancel.button')}
							onClick={() => props.onDismiss(false)}
						/>
						<Button type="primary" title={t('goals.subgoals.save.button')} onClick={save} />
					</FlexGap>
				</FlexGap>
			</DraggableCard>
		);
	},
	function EditSubgoalCardSkeleton(props: BoxProps) {
		return (
			<Card.Skeleton {...props}>
				<Heading.Skeleton />
			</Card.Skeleton>
		);
	},
);
